import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { defaultScoreType } from '../../Job/config';
import { useTranslations } from '../../context/TranslationContext';
import { getESCOData, saveAttribute } from '../service';
import { NO_STEP_ID } from '../const';

/**
 * steps => '[{id: string, name: string}]'
 * attributes => '{[dataSource]: [{value: string, text: string}]}'
 * evaluationCriteria => '[{step_id: string, items: [{attribute: string, question: string, score_type: string}]}]'
 * evaluatedSteps => '{[step_id]: [attribute_1, attribute_2, ...]}'
 */
const initialState = {
    attributes: {},
    attributesConfig: [],
    attributesList: {},
    attributesOptions: [],
    evaluatedSteps: {},
    evaluationCriteria: [],
    loading: true,
    ratingOptions: [],
    steps: [],
    stepsList: {},
};

const ACTION_UPDATE_STORE = 'updateStore';
const ACTION_UPDATE_EVALUATION_CRITERIA = 'updateEvaluationCriteria';
const ACTION_UPDATE_ATTRIBUTES = 'updateAttributes';
const ACTION_LOADING = 'loading';

const reducer = (state, { action, payload }) => {
    switch (action) {
        case ACTION_UPDATE_STORE:
            return { ...state, ...(typeof payload === 'function' ? payload(state) : payload) };
        case ACTION_UPDATE_EVALUATION_CRITERIA:
            return {
                ...state,
                evaluationCriteria: [
                    ...(typeof payload === 'function'
                        ? payload(state.evaluationCriteria)
                        : payload),
                ],
            };
        case ACTION_UPDATE_ATTRIBUTES:
            return {
                ...state,
                attributes: {
                    ...(typeof payload === 'function' ? payload(state.attributes) : payload),
                },
            };
        case ACTION_LOADING:
            return { ...state, loading: payload };
        default:
            return state;
    }
};

const useEvaluation = (additionalState = {}) => {
    const { t } = useTranslations();

    const [store, dispatch] = useReducer(reducer, { ...initialState, ...additionalState });
    const { evaluatedSteps, steps, attributes, evaluationCriteria, attributesList, stepsList, templateName } = store;
    const shouldProceed = useRef(true);

    const NO_STEP_TITLE = t('No step selected');

    const computedDataRef = useRef({
        hasSteps: false,
        hasAttributes: false,
    });

    const mapAttribute = (dataSource, text) => {
        const key = `${dataSource}:${text}`;
        return key;
    }

    const getMappedAttributes = useCallback((attributes) => {
        const mapped = {};
        const options = [];
        Object.keys(attributes).forEach((dataSource) => {
            attributes[dataSource].forEach(({ value = '', text }) => {
                const key = mapAttribute(dataSource, value);
                mapped[key] = text;
                options.push({ id: key, text });
            });
        });
        return { mappedAttributes: mapped, attributesOptions: options };
    }, [attributes]);

    useEffect(() => {
        const stepsList = steps.reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {});
        dispatch({
            action: ACTION_UPDATE_STORE,
            payload: (prevState) => ({
                ...prevState,
                stepsList,
            }),
        });
    }, [steps]);

    useEffect(() => {
        const { mappedAttributes, attributesOptions } = getMappedAttributes(attributes);
        updateStore({
            attributesList: mappedAttributes,
            attributesOptions: attributesOptions,
        });
    }, [attributes]);

    const alreadyAddedSteps = useMemo(
        () => evaluationCriteria.map(({ step_id }) => step_id),
        [evaluationCriteria]
    );

    const getStepOptions = useCallback(
        (currentStepId, includeNoStep = false) => {
            const filteredSteps = steps
                .filter(({ id }) => id === currentStepId || !alreadyAddedSteps.includes(id))
                .map(({ id, name }) => ({ id, text: name }))
            if (includeNoStep) {
                filteredSteps.unshift({ id: NO_STEP_ID, text: NO_STEP_TITLE });
            }
            return filteredSteps;
        },
        [steps, alreadyAddedSteps]
    );

    const anyStepRemaining = useMemo(() => !!getStepOptions().length, [getStepOptions]);

    const mappedEvaluatedSteps = useMemo(() => {
        return Object.entries(evaluatedSteps).reduce((acc, [step, attributes]) => {
            acc[step] = new Set(attributes);
            return acc;
        }, {});
    }, [evaluatedSteps]);

    const hasAnyStepEvaluated = useMemo(() => {
        return Object.values(evaluatedSteps).some((attributes) => attributes.length > 0);
    }, [evaluatedSteps]);

    const isStepEvaluated = useCallback(
        (step) => mappedEvaluatedSteps[step]?.size,
        [mappedEvaluatedSteps]
    );

    const isAttributeEvaluated = useCallback(
        (step, attribute) =>
            mappedEvaluatedSteps[step] && mappedEvaluatedSteps[step].has(attribute),
        [mappedEvaluatedSteps]
    );

    const onAddAttribute = useCallback(
        (attrType, { value, text }) => {
            if (
                attributes[attrType] &&
                attributes[attrType].find((attribute) => attribute.value === value)
            ) {
                notification(t('The {} already exists.').replace('{}', text));
                return;
            }

            dispatch({
                action: ACTION_UPDATE_ATTRIBUTES,
                payload: (prevAttributes) => {
                    const updatedAttributes = { ...prevAttributes };
                    if (!updatedAttributes[attrType]) updatedAttributes[attrType] = [];
                    updatedAttributes[attrType].push({ value, text });
                    return updatedAttributes;
                },
            });
        },
        [attributes]
    );

    const onRemoveAttribute = useCallback(
        (attr, value) => {
            if (!shouldProceed.current) return;
            shouldProceed.current = false;
            dispatch({
                action: ACTION_UPDATE_STORE,
                payload: (prevState) => {
                    const updatedAttributes = { ...prevState.attributes };
                    const updatedEvaluationCriteria = [];
                    if (updatedAttributes[attr]) {
                        updatedAttributes[attr] = updatedAttributes[attr].filter(
                            (attribute) => attribute.value !== value
                        );
                    }
                    for (const criteria of prevState.evaluationCriteria) {
                        if (criteria.items) {
                            const updatedItems = criteria.items.filter(
                                (item) => item.attribute !== mapAttribute(attr, value)
                            );
                            updatedEvaluationCriteria.push({
                                ...criteria,
                                items: updatedItems
                            });
                        }
                    }
                    shouldProceed.current = true;
                    return {
                        ...prevState,
                        attributes: updatedAttributes,
                        evaluationCriteria: updatedEvaluationCriteria
                    };
                },
            });
        },
        [attributes]
    );

    const onSkillAddHandler = useCallback(
        (attributeId, index) => {
            if (!attributeId || index === undefined) {
                notification(t('Unable to add data'));
                return;
            }

            if (evaluationCriteria[index]) {
                dispatch({
                    action: ACTION_UPDATE_EVALUATION_CRITERIA,
                    payload: (criteria) => {
                        const updatedItems = criteria[index]?.items || [];
                        if (!updatedItems.find((item) => item.attribute === attributeId)) {
                            const newCriteria = [...criteria];
                            newCriteria[index] = {
                                ...newCriteria[index],
                                items: [
                                    ...updatedItems,
                                    {
                                        attribute: attributeId || '',
                                        question: '',
                                        score_type: defaultScoreType,
                                    },
                                ],
                            };
                            return newCriteria;
                        }
                        return criteria;
                    },
                });
            }
        },
        [evaluationCriteria]
    );

    const onRemoveCriteriaHandler = useCallback(
        (index) => {
            dispatch({
                action: ACTION_UPDATE_EVALUATION_CRITERIA,
                payload: (prevCriteria) => {
                    const updatedCriteria = prevCriteria.filter((_, i) => i !== index);
                    return updatedCriteria;
                },
            });
        },
        [evaluationCriteria]
    );

    const onDuplicateCriteriaHandler = useCallback(
        (index) => {
            dispatch({
                action: ACTION_UPDATE_EVALUATION_CRITERIA,
                payload: (prevCriteria) => {
                    const rows = [...prevCriteria];
                    const duplicatedRow = JSON.parse(JSON.stringify(rows[index]));
                    const step = getStepOptions()[0];
                    if (step) {
                        duplicatedRow.step_id = step.id;
                        rows.push(duplicatedRow);
                    }
                    return rows;
                },
            });
        },
        [evaluationCriteria, getStepOptions]
    );

    const onAddEvaluationClickHandler = useCallback(
        (includeNoStep = false) => {
            const step = includeNoStep ? { id: NO_STEP_ID } : getStepOptions()[0];
            if (step) {
                dispatch({
                    action: ACTION_UPDATE_EVALUATION_CRITERIA,
                    payload: (prevCriteria) => {
                        return [...prevCriteria, { step_id: step.id, items: [] }];
                    },
                });
            }
        },
        [evaluationCriteria, getStepOptions]
    );

    const onUpdateCriteriaHandler = useCallback(
        (criteria, index) => {
            dispatch({
                action: ACTION_UPDATE_EVALUATION_CRITERIA,
                payload: (prevCriteria) => {
                    return [
                        ...prevCriteria.slice(0, index),
                        criteria,
                        ...prevCriteria.slice(index + 1),
                    ];
                },
            });
        },
        [evaluationCriteria]
    );

    const onSelectStepHandler = useCallback(
        (id, index) => {
            if (evaluationCriteria[index]) {
                dispatch({
                    action: ACTION_UPDATE_EVALUATION_CRITERIA,
                    payload: (prevCriteria) => {
                        return [
                            ...prevCriteria.slice(0, index),
                            {
                                ...prevCriteria[index],
                                step_id: id,
                            },
                            ...prevCriteria.slice(index + 1),
                        ];
                    },
                });
            }
        },
        [evaluationCriteria]
    );

    const updateStore = useCallback((data) => {
        dispatch({
            action: ACTION_UPDATE_STORE,
            payload: data,
        });
    }, []);

    const getAttributeTitleByKey = useCallback(
        (key) => {
            if (!key) return '';
            if (key in attributesList) return attributesList[key];
            const [_, ...values] = key.split(':');
            return values.join(':');
        },
        [attributesList]
    );

    const getStepTitleByKey = useCallback((key) => stepsList[key] || key[stepsList] || NO_STEP_TITLE, [stepsList, NO_STEP_TITLE]);

    const getEvaluationTemplatePayloadData = useCallback(() => {
        const mappedAttributesList = Object.entries(attributes).reduce((acc, [attr, values]) => {
            return acc.concat(values.map(({ value }) => `${attr}:${value || ''}`));
        }, []);
        return { templateName, attributes: mappedAttributesList, evaluationCriteria };
    }, [templateName, attributes, evaluationCriteria]);

    const validatePayloadData = useCallback(() => {
        const errors = [];
        const criteriaAttributes = evaluationCriteria.reduce((acc, criteria) => {
            return acc.concat(criteria.items.map(({ attribute }) => attribute));
        }, []);

        const attributesKeys = Object.entries(attributes).flatMap(([dataSource, values]) => values.map(({ value }) => `${dataSource}:${value}`));
        const difference = new Set(criteriaAttributes).difference(new Set(attributesKeys));
        if (!attributesKeys.length) {
            errors.push(t('No requirements item is selected.'));
        }
        if (difference.size) {
            errors.push(
                t(
                    'One or more requirements items are missing from evaluation steps. {attributes}',
                    { attributes: Array.from(difference).join(', ') }
                )
            );
        }

        // In job-edit view, there will be no template name.
        if (typeof templateName !== 'undefined' && templateName.length < 3) {
            errors.push(t('Template name must be at least 3 characters long.'));
        }
        return errors;
    }, [
        templateName,
        attributes,
        evaluationCriteria,
        getEvaluationTemplatePayloadData,
    ]);

    const setLoading = useCallback((loading) => {
        dispatch({
            action: ACTION_LOADING,
            payload: loading,
        });
    }, []);

    const autofillEvaluationCriteria = useCallback(() => {
        const attributesKeys = Object.keys(attributesList);
        const allStepsIds = new Set(Object.keys(stepsList));
        const newEvaluationCriteria = [];

        const fillData = (stepId) => {
            const stepEvaluationCriteria = evaluationCriteria.find(
                (criteria) => criteria.step_id === stepId
            );
            const existingItems = stepEvaluationCriteria?.items || [];
            const existingAttributes = new Set(existingItems.map((item) => item.attribute));
            const newItems = attributesKeys
                .filter((attributeKey) => !existingAttributes.has(attributeKey))
                .map((attributeKey) => ({
                    attribute: attributeKey,
                    question: '',
                    score_type: defaultScoreType,
                }));
            return {
                step_id: stepId,
                items: [...existingItems, ...newItems],
            };
        }
        const filledStepsIds = evaluationCriteria.map(({ step_id }) => step_id);
        const remainingStepsIds = allStepsIds.difference(new Set(filledStepsIds));
        newEvaluationCriteria.push(filledStepsIds.map(fillData));
        newEvaluationCriteria.push(Array.from(remainingStepsIds).map(fillData));
        dispatch({
            action: ACTION_UPDATE_EVALUATION_CRITERIA,
            payload: newEvaluationCriteria.flat(),
        });
    }, [attributesList, stepsList, evaluationCriteria]);

    useEffect(() => {
        computedDataRef.current.hasSteps = evaluationCriteria.length > 0;
        computedDataRef.current.hasAttributes = Object.keys(attributes).some((dataSource) => attributes[dataSource]?.length > 0);
    }, [attributes, evaluationCriteria]);

    const onAutofillEvaluationCriteria = useCallback(() => {

        if (!computedDataRef.current.hasSteps) {
            autofillEvaluationCriteria();
            return;
        }

        show_confirmation_overlay(
            'confirm-autofill-evaluation-criteria',
            t('Confirm autofill evaluation steps'),
            t('Are you sure you want to autofill the evaluation steps?'),
            { label: t('No') },
            {
                label: t('Yes, autofill'),
                action: autofillEvaluationCriteria,
            }
        );
    }, [computedDataRef.current, attributesList, stepsList, evaluationCriteria, attributes]);


    const saveAttributeHandler = async ({ id: attrType, result, cb, save_url, locale }) => {
        const data = result?.data || {};
        const { id, category, isNew, value, text: itemText } = data;
        const text = isNew ? value : itemText;

        if ((category === 'esco' || isNew) && !save_url) {
            notification(t('You do not have permission to add skills'));
            return;
        }

        let attribute;

        if (category === 'esco') {
            const ESCOData = await getESCOData(id, text, locale);
            if (!ESCOData) throw new Error(t('Failed to retrieve ESCO data. Please try again.'));
            attribute = await saveAttribute(save_url, { entries: [ESCOData] }, locale);
        } else if (isNew) {
            attribute = await saveAttribute(save_url, { entries: [{ default: text, [locale]: text }] }, locale);
        }

        if ((category === 'esco' || isNew)) {
            if (!attribute) throw new Error(t('Failed to save attribute. Please try again.'));
            onAddAttribute(attrType, { value: attribute.id, text: attribute.text });
        } else {
            onAddAttribute(attrType, { value: id, text });
        }

        cb?.();
    }

    return {
        ...store,
        anyStepRemaining,
        getAttributeTitleByKey,
        getStepOptions,
        isAttributeEvaluated,
        isStepEvaluated,
        onAddAttribute,
        onAddEvaluationClickHandler,
        onDuplicateCriteriaHandler,
        onRemoveAttribute,
        onRemoveCriteriaHandler,
        onSelectStepHandler,
        onSkillAddHandler,
        onUpdateCriteriaHandler,
        updateStore,
        validatePayloadData,
        getEvaluationTemplatePayloadData,
        setLoading,
        getStepTitleByKey,
        onAutofillEvaluationCriteria,
        saveAttributeHandler,
        computedData: computedDataRef.current,
        hasAnyStepEvaluated,
    };
};

export default useEvaluation;
